import type { MailboxInfo, MailboxKey } from 'owa-client-types';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { getMailboxKeyForMailboxInfo } from 'owa-client-types';
import type TableView from '../../store/schema/TableView';
import { getMailboxInfoFromRowKeyOrId } from './getMailboxInfoFromRowKeyOrId';

export interface MailboxGroup {
    mailboxInfo: MailboxInfo;
    rowKeys: string[];
}

/**
 * Groups selected row keys by their actual mailbox, keyed by MailboxKey for stable identity.
 * Only applicable when the table's mailboxInfo is a UnifiedInbox mailbox.
 * For non-UIB tables, returns a single group with the default mailboxInfo.
 *
 * Throws if any row key cannot be resolved to a mailbox — callers should not
 * silently drop unresolvable rows.
 */
export function groupRowKeysByMailboxInfo(
    defaultMailboxInfo: MailboxInfo,
    rowKeysOrIds: string[],
    tableView?: TableView
): Map<MailboxKey, MailboxGroup> {
    const groups = new Map<MailboxKey, MailboxGroup>();

    if (!isUnifiedInboxMailboxInfo(defaultMailboxInfo)) {
        // Non-UIB: single group with all rows
        const key = getMailboxKeyForMailboxInfo(defaultMailboxInfo);
        groups.set(key, {
            mailboxInfo: defaultMailboxInfo,
            rowKeys: [...rowKeysOrIds],
        });
        return groups;
    }

    for (const rowKeyOrId of rowKeysOrIds) {
        const mailboxInfo = getMailboxInfoFromRowKeyOrId(rowKeyOrId, tableView);

        if (!mailboxInfo) {
            throw new Error(
                'For UnifiedInbox, unable to resolve mailbox info for a row key or item ID.'
            );
        }

        const key = getMailboxKeyForMailboxInfo(mailboxInfo);
        const existing = groups.get(key);
        if (existing) {
            existing.rowKeys.push(rowKeyOrId);
        } else {
            groups.set(key, {
                mailboxInfo,
                rowKeys: [rowKeyOrId],
            });
        }
    }

    if (groups.size === 0) {
        throw new Error(
            'groupRowKeysByMailboxInfo: no groups produced \u2014 rowKeysOrIds was empty.'
        );
    }

    return groups;
}
